import {
  Text,
  Icon,
  Button,
  Dialog,
  TwSelector as Selector,
  HelperText,
  Link,
} from "@virkesborsen/ui-component-library";
import { formatPriceWithCurrency } from "../../../utils";
import { useState } from "react";
import { useDrone } from "../../../context/DroneContext";

export default function OrderSummary() {
  const {
    userProperties,
    addSelectedProperty,
    selectedProperties,
    removeSelectedProperty,
    selectedPackage,
    setSelectedPackage,
    selectedForestManagementPlan,
    setSelectedForestManagementPlan,
    couponDiscount,
    orderPrices,
    paymentMethod,
    errorMessages,
    setErrorMessages,
  } = useDrone();

  const [isSelectPropertyOpen, setIsSelectPropertyOpen] = useState(false);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);

  const totalNumberOfProperties = selectedProperties.length;
  const totalArea = selectedProperties.reduce(
    (acc, property) => acc + property.area,
    0
  );
  const firstThreeProperties = selectedProperties.slice(0, 3);
  const remainingProperties = selectedProperties.slice(3).length;
  const hasSelectedProperties = selectedProperties.length > 0;
  const showDivider = selectedPackage || selectedForestManagementPlan;

  const handleSelect = (option) => {
    const selectedProperty = userProperties.find(
      (prop) => prop.ref === option.value
    );
    if (!selectedProperties.some((p) => p.ref === selectedProperty.ref)) {
      addSelectedProperty(selectedProperty);
      setErrorMessages((prev) => ({ ...prev, properties: undefined }));
    } else {
      removeSelectedProperty(selectedProperty);
    }
  };

  return (
    <>
      <div className="flex flex-col w-full gap-6">
        <div className="flex flex-col w-full gap-4">
          <div className="flex flex-col gap-4">
            <Text variant="labelLargeMedium" className="pl-4">
              Sammanfattning
            </Text>
            <div className="w-full flex flex-col bg-white-50 rounded-xl p-4 gap-4">
              <div className="flex flex-row gap-2 justify-start items-start">
                <Icon
                  tw
                  name="MapPinSimple"
                  size="large"
                  spacing="medium"
                  className="fill-brown-700 bg-stone-200"
                />
                <div className="flex flex-col flex-1 h-full justify-center">
                  <div className="flex flex-col gap-1">
                    <Text variant="labelMediumMediumDense">
                      {hasSelectedProperties
                        ? `Fastighet (${totalNumberOfProperties})`
                        : "Inga fastigheter valda"}
                    </Text>
                    {hasSelectedProperties && (
                      <Text
                        variant="labelMediumRegularDense"
                        className="text-cloud-900 mb-3"
                      >
                        {totalArea} hektar
                      </Text>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {firstThreeProperties.map((property) => (
                      <Text
                        variant="labelMediumRegularDense"
                        className="text-pebble-500"
                      >
                        {property.designation}
                      </Text>
                    ))}
                    {remainingProperties > 0 && (
                      <Text
                        variant="labelMediumRegularDense"
                        className="text-pebble-500"
                      >
                        +{remainingProperties} till
                      </Text>
                    )}
                  </div>
                </div>
                <Button
                  tw
                  variant="secondary"
                  size="small"
                  onClick={() => setIsSelectPropertyOpen(true)}
                >
                  {hasSelectedProperties ? "Ändra" : "Lägg till"}
                </Button>
              </div>
              {hasSelectedProperties && (
                <>
                  <div className="w-full h-[0.5px] border border-stone-500/17" />

                  {/* Ortofoto selection */}
                  <div className="flex flex-row gap-2 justify-start items-start">
                    <Icon
                      tw
                      name="DroneSimple"
                      size="large"
                      spacing="medium"
                      className="fill-brown-700 bg-stone-200"
                    />
                    <div className="flex flex-col flex-1 h-full justify-center">
                      <div className="flex flex-col gap-1">
                        <Text variant="labelMediumMediumDense">Ortofoto</Text>

                        <Text
                          variant="labelMediumRegularDense"
                          className="text-cloud-900 mb-3"
                        >
                          Högupplösta bilder på din skog
                        </Text>
                      </div>
                    </div>
                  </div>

                  {/* Package selection */}
                  {selectedPackage && (
                    <div className="flex flex-row gap-2 justify-start items-start">
                      <Icon
                        tw
                        name="WarningFilled"
                        size="large"
                        spacing="medium"
                        className="fill-brown-700 bg-stone-200"
                      />
                      <div className="flex flex-col flex-1 h-full justify-center">
                        <div className="flex flex-col gap-1">
                          <Text variant="labelMediumMediumDense">
                            Skadeanalys {selectedPackage.title}
                          </Text>

                          <Text
                            variant="labelMediumRegularDense"
                            className="text-cloud-900 mb-3"
                          >
                            {selectedPackage.subtitle}
                          </Text>
                        </div>
                      </div>
                      <Button
                        tw
                        variant="secondary"
                        size="small"
                        onClick={() => setSelectedPackage(null)}
                      >
                        Ta bort
                      </Button>
                    </div>
                  )}

                  {/* Forest management plan selection */}
                  {selectedForestManagementPlan && (
                    <div className="flex flex-row gap-2 justify-start items-start">
                      <Icon
                        tw
                        name="FolderSimple"
                        size="large"
                        spacing="medium"
                        className="fill-brown-700 bg-stone-200"
                      />
                      <div className="flex flex-col flex-1 h-full justify-center">
                        <div className="flex flex-col gap-1">
                          <Text variant="labelMediumMediumDense">
                            Skogsbruksplan
                          </Text>

                          <Text
                            variant="labelMediumRegularDense"
                            className="text-cloud-900 mb-3"
                          >
                            Din skog i siffror - åtgärder, volym mm.
                          </Text>
                        </div>
                      </div>
                      <Button
                        tw
                        variant="secondary"
                        size="small"
                        onClick={() => setSelectedForestManagementPlan(null)}
                      >
                        Ta bort
                      </Button>
                    </div>
                  )}

                  <div className="w-full h-[0.5px] border border-stone-500/17" />

                  {/* Total price */}
                  <div className="flex flex-row gap-2 justify-between items-center">
                    <div className="flex flex-col gap-1">
                      <Text variant="labelMediumMediumDense">Totalbelopp</Text>
                      <Text
                        variant="labelMediumRegularDense"
                        className="text-pebble-500"
                      >
                        {formatPriceWithCurrency(orderPrices.discountPrice)}
                      </Text>
                    </div>
                    <Button
                      tw
                      variant="secondary"
                      size="small"
                      onClick={() => setIsOrderDetailsOpen(true)}
                    >
                      Detaljer
                    </Button>
                  </div>
                </>
              )}
            </div>
            {errorMessages.properties && (
              <HelperText
                icon="ExclamationFilled"
                label={errorMessages.properties}
                className={"text-danger-500 fill-danger-500 pl-4 pt-1-5"}
              />
            )}
          </div>
        </div>

        {hasSelectedProperties && (
          <>
            <div className="w-full h-[0.5px] border border-stone-500/17" />

            <div className="flex flex-col w-full gap-4">
              <Text variant="labelLargeMedium">Betalningsmetod</Text>
              <div className="w-full flex flex-col bg-white-50 rounded-xl p-4 gap-4">
                {paymentMethod === "klarna" && (
                  <div className="flex flex-col gap-4">
                    <img
                      alt="Klarna logo"
                      width="60px"
                      height="24px"
                      src={`${Window.static_path}img/payment-provider/klarna.png`}
                    />
                    <div className="flex flex-row gap-2 bg-stone-100 rounded-md p-3">
                      <Icon
                        name="InfoFilled"
                        size="medium"
                        className="fill-pebble-500"
                      />
                      <div className="flex flex-col gap-2">
                        <Text
                          variant="bodyExtraSmallRegular"
                          className="text-pebble-500"
                        >
                          Köp via Klarna visas med 25% moms inkluderad. Som
                          företag kan du ändå göra fullt momsavdrag även om det
                          inte syns vid checkout.
                          <br />
                          Du kan klicka på <b>Detaljer</b> ovan för att se mer
                          information.
                        </Text>
                      </div>
                    </div>

                    <Text variant="captionRegular">
                      Läs mer hos{" "}
                      <Link
                        tw
                        href="https://www.klarna.com/se/"
                        target="blank"
                        underline
                      >
                        Klarna.com/se
                      </Link>
                      {", "}
                      <Link
                        tw
                        href="https://www.klarna.com/se/villkor/"
                        target="blank"
                        underline
                      >
                        Klarnas köpvillkor
                      </Link>
                      {", "}
                      <Link
                        tw
                        href="https://www.klarna.com/se/dataskydd/"
                        target="blank"
                        underline
                      >
                        Klarnas sekretessmeddelande
                      </Link>
                      {" och "}
                      <Link
                        tw
                        href="https://www.klarna.com/se/cookies/"
                        target="blank"
                        underline
                      >
                        Klarnas cookie-meddelande
                      </Link>
                    </Text>

                    <div className="w-full h-[0.5px] border border-stone-500/17" />

                    <div className="flex flex-row gap-2">
                      <Text variant="captionRegular">
                        <Link
                          tw
                          href="https://treebula.se/terms/"
                          target="blank"
                          underline
                        >
                          Treebulas användarvillkor
                        </Link>
                        {", "}
                        <Link
                          tw
                          href="https://treebula.se/privacy/"
                          target="blank"
                          underline
                        >
                          Treebulas integritetspolicy
                        </Link>
                        .
                      </Text>
                    </div>
                  </div>
                )}
                {paymentMethod === "invoice" && (
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-row py-1 px-2 bg-charcoal-500 text-white-50 rounded-md w-fit">
                      <Text variant="labelSmallMedium">Faktura</Text>
                    </div>
                    <div className="flex flex-row gap-2 bg-stone-100 rounded-md p-3">
                      <Icon
                        name="InfoFilled"
                        size="medium"
                        className="fill-pebble-500"
                      />
                      <div className="flex flex-col gap-2">
                        <Text
                          variant="bodyExtraSmallRegular"
                          className="text-pebble-500"
                        >
                          Belopp över 20 000 kr betalas via faktura. Vi
                          kontaktar dig inom kort
                        </Text>
                      </div>
                    </div>

                    <div className="w-full h-[0.5px] border border-stone-500/17" />

                    <div className="flex flex-row gap-2">
                      <Text variant="captionRegular">
                        <Link
                          tw
                          href="https://treebula.se/terms/"
                          target="blank"
                          underline
                        >
                          Treebulas användarvillkor
                        </Link>
                        {", "}
                        <Link
                          tw
                          href="https://treebula.se/privacy/"
                          target="blank"
                          underline
                        >
                          Treebulas integritetspolicy
                        </Link>
                        .
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Select Property Dialog */}
      <Dialog isOpen={isSelectPropertyOpen} size="medium">
        <Dialog.Header>
          <div className="flex w-full justify-between">
            <Text variant="labelLargeMedium">Välj Fastighet</Text>
            <Button
              tw
              variant="secondary"
              size="icon"
              onClick={() => setIsSelectPropertyOpen(false)}
            >
              <Icon
                tw
                name="CrossSimple"
                className="color-charcoal-500"
                size="medium"
              />
            </Button>
          </div>
        </Dialog.Header>
        <Dialog.Content>
          <Selector>
            {userProperties?.map((option) => (
              <Selector.Option
                key={option.ref}
                label={`${option.designation} ${
                  option.type !== "PROP" ? "(Ej fastighet)" : ""
                }`}
                value={option.ref}
                isSelected={selectedProperties?.some(
                  (p) => p.ref === option.ref
                )}
                onClick={handleSelect}
                disabled={option.type !== "PROP"}
              />
            ))}
          </Selector>
        </Dialog.Content>
        <Dialog.Footer>
          <Button
            tw
            variant="primary"
            onClick={() => setIsSelectPropertyOpen(false)}
          >
            Klar
          </Button>
        </Dialog.Footer>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog isOpen={isOrderDetailsOpen} size="small">
        <Dialog.Header>
          <div className="flex w-full justify-between">
            <Text variant="labelLargeMedium">Prisdetaljer</Text>
            <Button
              tw
              variant="secondary"
              size="icon"
              onClick={() => setIsOrderDetailsOpen(false)}
            >
              <Icon
                tw
                name="CrossSimple"
                className="color-charcoal-500"
                size="medium"
              />
            </Button>
          </div>
        </Dialog.Header>
        <Dialog.Content>
          <div className="flex flex-col gap-4 h-full">
            <div className="flex flex-col gap-4">
              {totalArea <= 30 && (
                <div className="flex flex-row gap-2 bg-stone-100 rounded-md p-3">
                  <Icon name="InfoFilled" size="medium" />
                  <div className="flex flex-col gap-2">
                    <Text variant="bodyExtraSmallRegular">
                      För att kunna genomföra en kostnadseffektiv pilotutkörning
                      och täcka resekostnader samt övriga fasta moment i
                      uppstarten, kan vi tyvärr aldrig ta betalt för mindre än
                      30 hektar
                    </Text>
                  </div>
                </div>
              )}
              <div className="flex flex-row gap-2 justify-between items-center">
                <Text variant="labelMediumRegularDense">
                  Ortofoto ({orderPrices.propertiesHectarePrice}/ha)
                </Text>
                <Text variant="labelMediumMediumDense">
                  {formatPriceWithCurrency(orderPrices.propertiesTotalPrice)}
                </Text>
              </div>

              {showDivider && (
                <div className="w-full h-[0.5px] border border-stone-500/17" />
              )}

              {selectedPackage && (
                <div className="flex flex-row gap-2 justify-between items-center">
                  <Text variant="labelMediumRegularDense">
                    Skadeanalys {selectedPackage.title} (
                    {orderPrices.packageHectarePrice}/ha)
                  </Text>
                  <Text variant="labelMediumMediumDense">
                    {formatPriceWithCurrency(orderPrices.packageTotalPrice)}
                  </Text>
                </div>
              )}

              {selectedForestManagementPlan && (
                <div className="flex flex-row gap-2 justify-between items-center">
                  <Text variant="labelMediumRegularDense">
                    Skogsbruksplan (99 kr/ha)
                  </Text>
                  <Text variant="labelMediumMediumDense">
                    {formatPriceWithCurrency(
                      orderPrices.forestManagementPlanTotalPrice
                    )}
                  </Text>
                </div>
              )}

              <div className="w-full h-[0.5px] border border-stone-500/17" />

              {couponDiscount && (
                <div className="flex flex-row gap-2 justify-between items-center">
                  <Text variant="labelMediumRegularDense">
                    Rabattkod {couponDiscount.coupon_code}
                  </Text>
                  <Text variant="labelMediumMediumDense">
                    -
                    {formatPriceWithCurrency(
                      couponDiscount.coupon_discount_price
                    )}
                  </Text>
                </div>
              )}
              <div className="flex flex-row gap-2 justify-between items-center">
                <Text
                  variant="labelMediumRegularDense"
                  className="text-charcoal-300"
                >
                  Moms
                </Text>
                <Text variant="labelMediumMediumDense">
                  {formatPriceWithCurrency(orderPrices.tax)}
                </Text>
              </div>
              <div className="flex flex-row gap-2 justify-between items-center">
                <Text
                  variant="labelMediumRegularDense"
                  className="text-charcoal-300"
                >
                  Inkl. moms
                </Text>
                <Text variant="labelMediumMediumDense">
                  {formatPriceWithCurrency(
                    orderPrices.discountPrice + orderPrices.tax
                  )}
                </Text>
              </div>
            </div>

            <div className="w-full h-[0.5px] border border-stone-500/17 mt-auto" />

            <div className="flex flex-row gap-2 justify-between items-center">
              <Text variant="labelMediumMediumDense">Totalt</Text>
              <div className="flex flex-col gap-1 items-end">
                <Text variant="labelMediumMediumDense">
                  {formatPriceWithCurrency(orderPrices.discountPrice)}
                </Text>
                <Text
                  variant="labelXtraSmallRegular"
                  className="text-charcoal-300"
                >
                  exkl. moms
                </Text>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>
    </>
  );
}
